import { useState, useCallback } from 'react';
import { DesktopNotificationDetails } from '@weave/schema-gen-ts/dist/schemas/notification/v1/notification_api.pb';
import { useQueryClient } from 'react-query';
import { TaskCenterConstants, TaskCenterTypes } from '@frontend/api-task-center';
import { UsersQueries } from '@frontend/api-users';
import { getUser, isWeaveUser } from '@frontend/auth-helpers';
import { DEFAULT_FILTER_VALUES, TaskCenterTabs } from '../constants';
import { TaskCenterContext, SelectedFilters, taskModeKeys } from '../types';
import { useGetFilteredLocations } from './use-get-filtered-locations';

export const DEFAULT_CONTEXT_VALUE: TaskCenterContext = {
  taskMode: null,
  setTaskMode: (() => {}) as React.Dispatch<React.SetStateAction<taskModeKeys>>,
  locationIds: [],
  assignees: [],
  locationsData: [],
  shouldShowLocationDropdown: false,
  selectedFilters: { ...DEFAULT_FILTER_VALUES },
  setFilters: () => {},
  selectedTask: null,
  setSelectedTask: () => {},
  selectedTab: TaskCenterTabs[0].controller,
  setTab: () => {},
  taskId: null,
  setTaskId: () => {},
  setAssigneeFilter: () => {},
  assigneeFilter: '',
  isTaskCenterOpen: false,
  setIsTaskCenterOpen: () => {},
  resetFilters: () => {},
  onViewNotification: () => {},
};

export const useGetContextValues = (): TaskCenterContext => {
  const user = getUser();
  const queryClient = useQueryClient();
  const weaveUser = isWeaveUser();
  // Static values
  const [taskMode, setTaskMode] = useState<taskModeKeys>(null);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<(typeof TaskCenterTabs)[number]['controller']>(
    TaskCenterTabs[0].controller
  );
  const [selectedTask, setSelectedTask] = useState<TaskCenterTypes.Task | null>(null);
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>({ ...DEFAULT_FILTER_VALUES });

  const [assignee, setAssignee] = useState<string>('');
  const [isTaskCenterOpen, setIsTaskCenterOpen] = useState<boolean>(false);

  // Intentionally using it like this
  const locationsData = useGetFilteredLocations();

  const locationIds = locationsData.map((loc) => loc.id);

  const shouldShowLocationDropdown = locationsData.length > 1 && locationsData.length > 1;

  // Dynamic values for the context as per the data fetched, sorting and filters
  const { data: assignees } = UsersQueries.useGetUsersInLocations(locationIds);

  // Setting the values for the context
  const setFilters = useCallback((values: SelectedFilters) => setSelectedFilters(values), [setSelectedFilters]);
  const setTab = useCallback(
    (tab: (typeof TaskCenterTabs)[number]['controller']) => setSelectedTab(tab),
    [setSelectedTab]
  );

  const setAssigneeFilter = useCallback((value: string | '') => setAssignee(value), [setAssignee]);

  const onViewNotification = (notificationPayload: DesktopNotificationDetails['taskAlert']) => {
    if (notificationPayload?.taskId) {
      if (notificationPayload.taskId === taskId) {
        queryClient.removeQueries({
          queryKey: [TaskCenterConstants.TASK_BY_ID_QUERY_KEY, taskId],
        });
      }
      if (!notificationPayload?.body || notificationPayload?.notificationType?.includes('Task Updated')) {
        setTaskId?.(notificationPayload?.taskId ?? '');
        setTaskMode?.('details');
      } else {
        if (selectedFilters.sortType !== TaskCenterTypes.SortField.SORT_DUE_DATE)
          setSelectedFilters({ ...selectedFilters, sortType: TaskCenterTypes.SortField.SORT_DUE_DATE });
      }
    }
    // Open task center in all cases
    setIsTaskCenterOpen(true);
  };

  return {
    taskMode,
    setTaskMode,
    selectedTask,
    setSelectedTask,
    locationIds,
    assignees: assignees?.users ?? [],
    locationsData,
    shouldShowLocationDropdown,
    selectedFilters,
    setFilters,
    selectedTab,
    setTab,
    taskId,
    setTaskId,
    setAssigneeFilter,
    assigneeFilter: assignee,
    isTaskCenterOpen,
    setIsTaskCenterOpen,
    resetFilters: () => {
      setSelectedFilters({ ...DEFAULT_FILTER_VALUES });
      setAssigneeFilter(!weaveUser ? user?.userID ?? '' : '');
    },
    onViewNotification,
  };
};
